import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart,   RadarChart, Legend } from 'recharts';
import { Calendar, Clock, AlertTriangle, Users, TrendingUp, Filter, CheckCircle, XCircle, Timer, AlertCircle, Info, LayoutDashboard,   CalendarDays,   Folder  } from 'lucide-react';
import { number } from 'motion-dom';
import api from '../../api';

const Dashboard = () => {
  const [selectedProject, setSelectedProject] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [developers, setDevelopers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [moduleId, setModuleId] = useState(null);


  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

const filterByDate = (items) => {
  if (selectedMonth === 'all') return items;
  
  return items.filter(item => {
    if (!item.created_at && !item.date_livraison && !item.last_update) return false;
    
    const itemDate = new Date(item.created_at || item.date_livraison || item.last_update);
    return itemDate.getMonth() === parseInt(selectedMonth) && 
           itemDate.getFullYear() === currentYear;
  });
};

  useEffect(() => {
    fetchData();
  }, []);

      useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setModuleId(parseInt(user.module_id, 10));

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

const fetchData = async () => {
  try {
    setLoading(true);

    // Appels API en parallèle avec axios
    const [developersResponse, ticketsResponse] = await Promise.all([
      api.get(`/utilisateurs/developpeurs/module/${moduleId}`),
      api.get(`/tickets/modules/${moduleId}`)
    ]);

    // Données des développeurs
    if (developersResponse.data.developpeurs) {
      setDevelopers(developersResponse.data.developpeurs);
    }

    // Données des tickets avec filtre
    if (ticketsResponse.data.tickets) {
      const filteredTickets = ticketsResponse.data.tickets.filter(ticket => {
        const validationAdminCl = String(ticket.validation_adminCl);

        if (ticket.type === 'demande' && ticket.demande) {
          const confirmationAdmin = String(ticket.demande.confirmation_admin);
          const accepter = String(ticket.demande.accepter);
          return (confirmationAdmin === '1' && accepter === '1' && validationAdminCl === '4');
        }

        return (ticket.type === 'bug' && validationAdminCl === '4');
      });

      setTickets(filteredTickets);
    }

  } catch (err) {
    setError('Erreur lors du chargement des données');
    console.error('Error fetching data:', err);
  } finally {
    setLoading(false);
  }
};


  // ===================== UTILITAIRES DE CALCUL =====================

  /**
   * Calcule le délai moyen de résolution basé sur date_creation et date_livraison
   * Uniquement pour les tickets livrés (etape_id = 7)
   */
  const calculateAverageResolutionTime = () => {
    const deliveredTickets = filterByDate(tickets.filter(ticket => 
      ticket.etape_id === 7 && ticket.created_at && ticket.date_livraison
    ));
    
    if (deliveredTickets.length === 0) return 0;
    
    const totalDays = deliveredTickets.reduce((acc, ticket) => {
      const created = new Date(ticket.created_at);
      const delivered = new Date(ticket.date_livraison);
      const days = Math.ceil((delivered - created) / (1000 * 60 * 60 * 24));
      return acc + Math.max(days, 0); // Éviter les valeurs négatives
    }, 0);
    
    return (totalDays / deliveredTickets.length).toFixed(1);
  };

  /**
   * Calcule les tickets d'échéance aujourd'hui avec la cause
   */
  const getTodayDeadlinesWithCause = () => {
    const today = new Date().toISOString().split('T')[0];
    
    return filterByDate(tickets.filter(ticket => {
      const clientDeadline = ticket.deadline_client;
      const techDeadline = ticket.deadline_tech;
      
      return (clientDeadline && clientDeadline.startsWith(today)) || 
             (techDeadline && techDeadline.startsWith(today));
    }).map(ticket => {
      const clientToday = ticket.deadline_client && ticket.deadline_client.startsWith(today);
      const techToday = ticket.deadline_tech && ticket.deadline_tech.startsWith(today);
      
      let cause = '';
      if (clientToday && techToday) cause = 'Les deux';
      else if (clientToday) cause = 'Client';
      else if (techToday) cause = 'Technique';
      
      return {
        id: ticket.id,
        title: ticket.nom,
        consultant: ticket.affectee_par ? `${ticket.affectee_par.prenom} ${ticket.affectee_par.nom} `: 'Non assigné',
        priority: ticket.priorite === '1' ? 'high' : ticket.priorite === '2' ? 'medium' : 'low',
        cause: cause
      };
    }));
  };

  /**
   * Calcule les tickets avec deadline dépassée
   */
  const getOverdueTickets = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return filterByDate(tickets).filter(ticket => {
      const clientDeadline = ticket.deadline_client ? new Date(ticket.deadline_client) : null;
      const techDeadline = ticket.deadline_tech ? new Date(ticket.deadline_tech) : null;
      
      if (clientDeadline) clientDeadline.setHours(0, 0, 0, 0);
      if (techDeadline) techDeadline.setHours(0, 0, 0, 0);
      
      return (clientDeadline && clientDeadline < today) || 
             (techDeadline && techDeadline < today);
    }).map(ticket => {
      const clientDeadline = ticket.deadline_client ? new Date(ticket.deadline_client) : null;
      const techDeadline = ticket.deadline_tech ? new Date(ticket.deadline_tech) : null;
      
      let daysOverdue = 0;
      let overdueType = '';
      
      if (clientDeadline) clientDeadline.setHours(0, 0, 0, 0);
      if (techDeadline) techDeadline.setHours(0, 0, 0, 0);
      
      const clientOverdue = clientDeadline && clientDeadline < today;
      const techOverdue = techDeadline && techDeadline < today;
      
      if (clientOverdue && techOverdue) {
        const clientDays = Math.ceil((today - clientDeadline) / (1000 * 60 * 60 * 24));
        const techDays = Math.ceil((today - techDeadline) / (1000 * 60 * 60 * 24));
        daysOverdue = Math.max(clientDays, techDays);
        overdueType = 'Client & Technique';
      } else if (clientOverdue) {
        daysOverdue = Math.ceil((today - clientDeadline) / (1000 * 60 * 60 * 24));
        overdueType = 'Client';
      } else if (techOverdue) {
        daysOverdue = Math.ceil((today - techDeadline) / (1000 * 60 * 60 * 24));
        overdueType = 'Technique';
      }
      
      return {
        id: ticket.id,
        title: ticket.nom,
        consultant: ticket.affectee_par ? `${ticket.affectee_par.prenom} ${ticket.affectee_par.nom}` : 'Non assigné',
        daysOverdue: daysOverdue,
        overdueType: overdueType,
        priority: ticket.priorite === '1' ? 'high' : ticket.priorite === '2' ? 'medium' : 'low'
      };
    });
  };

  /**
   * Calcule les tickets bloquants (étapes 4 et 5 uniquement)
   */
  const getBlockingTickets = () => {
    const currentDate = new Date();
    
    return filterByDate(tickets).filter(ticket => {
      return ticket.etape_id === 4 || ticket.etape_id === 5; // aide fonctionnel ou attente client
    }).map(ticket => {
      let duration = '0j';
      
      if (ticket.last_update) {
        const lastUpdate = new Date(ticket.last_update);
        const days = Math.ceil((currentDate - lastUpdate) / (1000 * 60 * 60 * 24));
        duration = `${days}j`;
      }
      
      const reason = ticket.etape_id === 5 ? 'Attente client' : 'Aide fonctionnel';
      
      return {
        id: ticket.id,
        title: ticket.nom,
        consultant: ticket.affectee_par ? `${ticket.affectee_par.prenom} ${ticket.affectee_par.nom}` : 'Non assigné',
        duration,
        reason,
        priority: ticket.priorite === '1' ? 'high' : ticket.priorite === '2' ? 'medium' : 'low'
      };
    });
  };

  /**
   * Calcule la tendance de résolution sur 7 jours (tickets livrés par date_livraison)
   */
  // const getResolutionTrend = () => {
  //   const last7Days = [];
  //   const today = new Date();
    
  //   for (let i = 6; i >= 0; i--) {
  //     const date = new Date(today);
  //     date.setDate(today.getDate() - i);
  //     const dateStr = date.toISOString().split('T')[0];
      
  //     const deliveredTickets = tickets.filter(ticket => {
  //       return ticket.etape_id === '7' && 
  //              ticket.date_livraison && 
  //              ticket.date_livraison.startsWith(dateStr);
  //     }).length;
      
  //     last7Days.push({
  //       day: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
  //       tickets: deliveredTickets,
  //       date: dateStr
  //     });
  //   }
    
  //   return last7Days;
  // };
const getResolutionTrend = () => {
  const last7Days = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    
    const deliveredTickets = filterByDate(
      tickets.filter(ticket => {
        return Number(ticket.etape_id) === 7 && 
               ticket.date_livraison && 
               ticket.date_livraison.startsWith(dateStr);
      }),
      'date_livraison'
    ).length;
    
    last7Days.push({
      day: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
      tickets: deliveredTickets,
      date: dateStr
    });
  }
  
  return last7Days;
};
  /**
   * Calcule l'efficacité d'un développeur selon les nouvelles règles
   */
 const calculateDeveloperEfficiency = (developer) => {
  const devTickets = filterByDate(developer.tickets_developpeur || []);
  
  if (devTickets.length === 0) return 0;
  
  const closedTickets = devTickets.filter(t => (t.etape_id === 3 && t.status === 3) || t.etape_id === 6 || t.etape_id === 7);
  const totalTickets = devTickets.length;
  
  // 1. احترام المواعيد (40%)
  let deadlineScore = 0;
  const ticketsWithDeadline = closedTickets.filter(t => t.deadline_tech && t.date_livraison);
  
  if (ticketsWithDeadline.length > 0) {
    const onTimeTickets = ticketsWithDeadline.filter(ticket => {
      const deadline = new Date(ticket.deadline_tech);
      const delivered = new Date(ticket.date_livraison);
      
      // إذا تم التسليم قبل الموعد النهائي
      if (delivered < deadline) {
        ticket.deadlineStatus = 'early';
        return true;
      }
      // إذا تم التسليم في الموعد النهائي بالضبط
      else if (delivered.getTime() === deadline.getTime()) {
        ticket.deadlineStatus = 'on-time';
        return true;
      }
      // إذا تأخر التسليم
      else {
        ticket.deadlineStatus = 'late';
        return false;
      }
    });
    
    // حساب النقاط بناء على الشروط المطلوبة
    deadlineScore = ticketsWithDeadline.reduce((acc, ticket) => {
      const deadline = new Date(ticket.deadline_tech);
      const delivered = new Date(ticket.date_livraison);
      
      if (ticket.deadlineStatus === 'early') {
        return acc + 10; // أقصى درجة للتسليم المبكر
      } 
      else if (ticket.deadlineStatus === 'on-time') {
        return acc + 5; // درجة متوسطة للتسليم في الموعد
      }
      else {
        // حساب التأخير بالأيام
        const daysLate = Math.ceil((delivered - deadline) / (1000 * 60 * 60 * 24));
        // خصم 5 نقاط عن كل يوم تأخير
        const points = Math.max(0, 20 - (daysLate * 5));
        return acc + points;
      }
    }, 0) / ticketsWithDeadline.length;
  }
  
  // 2. معدل الإنجاز (30%)
  const completionScore = (closedTickets.length / totalTickets) * 6;
  
  // 3. السرعة (20%)
  let speedScore = 0;
  const ticketsWithDates = closedTickets.filter(t => t.date_creation && t.date_livraison);
  
  if (ticketsWithDates.length > 0) {
    const avgDays = ticketsWithDates.reduce((acc, ticket) => {
      const created = new Date(ticket.date_creation);
      const delivered = new Date(ticket.date_livraison);
      const days = Math.ceil((delivered - created) / (1000 * 60 * 60 * 24));
      return acc + Math.max(days, 0);
    }, 0) / ticketsWithDates.length;
    
    // Score inversé: moins de jours = meilleur score
    speedScore = Math.max(0, 5 - (avgDays / 2));
  }
  
  // 4. إدارة الأولويات (10%)
  const highPriorityTickets = devTickets.filter(t => t.priorite === 1);
  const highPriorityCompleted = highPriorityTickets.filter(t => t.etape_id === 3);
  let priorityScore = 0;
  
  if (highPriorityTickets.length > 0) {
    priorityScore = (highPriorityCompleted.length / highPriorityTickets.length) * 5;
  } else {
    // إذا لم يكن هناك تذاكر عالية الأولوية
    priorityScore = 2.5; // نصف الدرجة
  }
  
  // حساب النتيجة النهائية
  let totalScore = deadlineScore + completionScore + speedScore + priorityScore;
  
  // إذا لم يكن هناك أي تذاكر تجاوزت الموعد النهائي
  const hasNoOverdueTickets = ticketsWithDeadline.every(t => t.deadlineStatus !== 'late');
  if (hasNoOverdueTickets && ticketsWithDeadline.length > 0) {
    totalScore += 2; // مكافأة إضافية لعدم وجود تأخير
  }
  
  return Math.round(Math.min(Math.max(totalScore, 0), 100));
};

  /**
   * Calcule le délai moyen d'un développeur
   */
  const calculateDeveloperAverageDelay = (developer) => {
    const devTickets = filterByDate(developer.tickets_developpeur || []);
  const closedTickets = devTickets.filter(t => (t.etape_id === 3 && t.status === 3) || t.etape_id === 6 || t.etape_id === 7);

    
    if (closedTickets.length === 0) return 'N/A';
    
    const totalDays = closedTickets.reduce((acc, ticket) => {
      const deadline = new Date(ticket.deadline_tech);
      const started = new Date(ticket.date_debut_tech);
      const days = Math.ceil((deadline - started) / (1000 * 60 * 60 * 24));
      return acc + Math.max(days, 0);
    }, 0);
    
    return (totalDays / closedTickets.length).toFixed(1) + 'j';
  };

  // ===================== DONNÉES CALCULÉES =====================

   const processTicketsData = () => {
    const filteredTickets = filterByDate(tickets);
    const totalTickets = filteredTickets.length;
    const closedTickets1 = filteredTickets.filter(ticket => ticket.etape_id === 1);
    const closedTickets2 = filteredTickets.filter(ticket => ticket.etape_id === 2);
    const closedTickets = closedTickets1.length + closedTickets2.length;
    const deliveredTickets = filteredTickets.filter(ticket => ticket.etape_id === 7).length;
    const inProgressTickets = filteredTickets.filter(ticket => 
      ticket.etape_id && ![2,7,1].includes(ticket.etape_id)
    ).length;
    
    return {
      total: totalTickets,
      closed: closedTickets,
      delivered: deliveredTickets,
      inProgress: inProgressTickets,
      avgResolutionTime: calculateAverageResolutionTime(),
      todayDeadlines: getTodayDeadlinesWithCause().length
    };
  };

  

  const getConsultantsData = () => {
    return developers.map(dev => {
      const devTickets = filterByDate(dev.tickets_developpeur || []);
      const efficiency = calculateDeveloperEfficiency(dev);
      const avgDelay = calculateDeveloperAverageDelay(dev);

          // Calcul du total des retours
    const totalRetours = devTickets.reduce((sum, ticket) => {
      return sum + (ticket.nbr_retours_fonctionnels || 0);
    }, 0);
      
      return {
        name: `${dev.prenom} ${dev.nom}`,
        tickets: devTickets.length,
        efficiency: efficiency,
        avgDelay: avgDelay,
        id: dev.utilisateur_id,
        closedTickets : devTickets.filter(t =>
  (t.etape_id === 3 && t.status === 3) ||
  t.etape_id === 6 ||
  t.etape_id === 7
).length,
        inProgressTickets: devTickets.filter(t => t.etape_id && ![3, 6, 7].includes(t.etape_id)).length,
        deliveredTickets: devTickets.filter(t => t.etape_id === 7).length,
        waitingTickets: devTickets.filter(t => t.etape_id === 4 || t.etape_id === 5 ).length,
        totalRetours: totalRetours // Ajout du total des retours

      };
    });
  };




  const ticketsData = processTicketsData();
  const consultantsData = getConsultantsData();
  const todayDeadlines = getTodayDeadlinesWithCause();
  const overdueTickets = getOverdueTickets();
  const blockingTickets = getBlockingTickets();
  const resolutionTrend = getResolutionTrend();


  const statusDistribution = [
    { name: 'En Attente', value: ticketsData.closed, color: '#10b981' },
    { name: 'En Cours', value: ticketsData.inProgress, color: '#f59e0b' },
    { name: 'Livrés', value: ticketsData.delivered, color: '#3b82f6' }
  ];



  const projects = [
    { id: 'all', name: 'Tous les projets' },
    ...developers.map(dev => ({
      id: dev.utilisateur_id.toString(),
      name: `${dev.prenom} ${dev.nom}`
    }))
  ];

  const KPICard = ({ title, value, subtitle, icon: Icon, color, tooltip }) => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 relative group">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {tooltip && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Info className="w-4 h-4 text-gray-400" />
            <div className="absolute top-0 right-0 bg-black text-white p-2 rounded text-xs w-48 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <div className="text-3xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-600">{title}</div>
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 text-lg">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">

      {/* Header */}

<div className="mb-8">
  {/* Header Section avec filtres intégrés */}
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
    {/* Titre et description */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3">
        <LayoutDashboard className="w-6 h-6 text-blue-500" />
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 truncate">Dashboard Chef de Projet</h1>
      </div>
      <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
        <Info className="w-3.5 h-3.5 text-gray-400" />
        <span className="truncate">Vue d'ensemble des projets et performance de l'équipe</span>
      </p>
    </div>
    
    {/* Filtres compacts */}
    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
      {/* Filtre mois/année groupés */}
      <div className="flex gap-2">
        <div className="relative">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 pl-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            <option value="all">Tous mois</option>
            {Array.from({length: 12}, (_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString('fr-FR', {month: 'short'})}
              </option>
            ))}
          </select>
          <Calendar className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        
        <div className="relative">
          <select
            value={currentYear}
            onChange={(e) => setCurrentYear(parseInt(e.target.value))}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 pl-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            {Array.from({length: 5}, (_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
          <CalendarDays className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Date/Heure et bouton reset */}
      <div className="flex items-center gap-3">
        {(selectedMonth !== 'all') && (
          <button
            onClick={() => {
              setSelectedMonth('all');
              setCurrentYear(new Date().getFullYear());
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-all"
          >
            <XCircle className="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        )}
        
        <div className="hidden sm:flex items-center gap-2 text-sm bg-blue-50/50 px-3 py-1.5 rounded-lg border border-blue-100">
          <Clock className="w-4 h-4 text-blue-500" />
          <span className="font-medium text-gray-700">
            {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className="text-gray-500">|</span>
          <CalendarDays className="w-4 h-4 text-blue-500" />
          <span className="text-gray-600">
            {currentTime.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
          </span>
        </div>
      </div>
    </div>
  </div>
</div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          title="Total Tickets"
          value={ticketsData.total}
          subtitle="Tous les tickets dans le système pour vous module"
          icon={CheckCircle}
          color="bg-gradient-to-r from-blue-500 to-blue-600"
          tooltip="Nombre total de tickets dans le système"
        />
        <KPICard
          title="Délai Moyen Résolution"
          value={`${ticketsData.avgResolutionTime}j`}
          subtitle="Resolution moyenne des tickets livrés"
          icon={Timer}
          color="bg-gradient-to-r from-emerald-500 to-emerald-600"
          tooltip="Temps moyen entre date_creation et date_livraison pour les tickets livrés (étape 7)"
        />
        <KPICard
          title="Échéances Aujourd'hui"
          value={ticketsData.todayDeadlines}
          subtitle="Deadline client/technique"
          icon={Calendar}
          color="bg-gradient-to-r from-purple-500 to-purple-600"
          tooltip="Tickets dont la deadline_client ou deadline_tech est aujourd'hui"
        />
        <KPICard
          title="Tickets Bloquants"
          value={blockingTickets.length}
          subtitle="Étapes En Attente Client ou Aide Fonctionnel"
          icon={AlertTriangle}
          color="bg-gradient-to-r from-red-500 to-red-600"
          tooltip="Tickets en étape 'aide fonctionnel' (4) ou 'attente client' (5)"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Distribution des tickets */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            Répartition par Étape
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-4">
            {statusDistribution.map((item, index) => (
              <div key={index} className="flex items-center text-sm">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span>{item.name}: {item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tickets par consultant */}
<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
  <h3 className="text-lg font-semibold mb-4 flex items-center">
    <Users className="w-5 h-5 mr-2 text-blue-500" />
    État des Tickets par Consultant
  </h3>
  <div className="h-64">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={consultantsData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis dataKey="name" type="category" width={80} fontSize={12} />
        <Tooltip />
        <Legend />
        <Bar dataKey="closedTickets" stackId="a" fill="#10b981" name="Clôturés" />
        <Bar dataKey="deliveredTickets" stackId="a" fill="#3b82f6" name="Livrés" />
        <Bar dataKey="inProgressTickets" stackId="a" fill="#f59e0b" name="En cours" />
        <Bar dataKey="waitingTickets" stackId="a" fill="#ef4444" name="En attente" />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>

        {/* Tendance résolution */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
            Tickets Livrés (7 derniers jours)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={resolutionTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="tickets" 
                  stroke="#3b82f6" 
                  fill="url(#colorGradient)" 
                />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Échéances */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-500" />
              Échéances Aujourd'hui
            </div>
            <div className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
              {todayDeadlines.length}
            </div>
          </h3>
          
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {todayDeadlines.length > 0 ? todayDeadlines.map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{ticket.title}</div>
                  <div className="text-sm text-gray-600">{ticket.consultant}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                    ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {ticket.priority === 'high' ? 'Haute' : ticket.priority === 'medium' ? 'Moyenne' : 'Basse'}
                  </div>
                  <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                    {ticket.cause}
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center text-gray-500 py-8">
                Aucune échéance aujourd'hui
              </div>
            )}
          </div>
        </div>

        {/* Dépassement Deadline */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-orange-500" />
              Dépassement Deadline
            </div>
            <div className="text-sm bg-orange-100 text-orange-700 px-3 py-1 rounded-full ">
              {overdueTickets.length}
            </div>
          </h3>
          
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {overdueTickets.length > 0 ? overdueTickets.map((ticket) => (
              <div key={ticket.id} className="p-4 border-l-4 border-orange-400 bg-orange-50 rounded-r-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">{ticket.title}</div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-1" />
                    {ticket.daysOverdue}j
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                    ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {ticket.priority === 'high' ? 'Haute' : ticket.priority === 'medium' ? 'Moyenne' : 'Basse'}
                  </div>
                  <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {ticket.overdueType}
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center text-gray-500 py-8">
                Aucun dépassement de deadline
              </div>
            )}
          </div>
        </div>
        {/* Tickets Bloquants */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
              Tickets Bloquants
            </div>
            <div className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded-full">
              {blockingTickets.length}
            </div>
          </h3>
          
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {blockingTickets.length > 0 ? blockingTickets.map((ticket) => (
              <div key={ticket.id} className="p-4 border-l-4 border-red-400 bg-red-50 rounded-r-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">{ticket.title}</div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-1" />
                    {ticket.duration}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    ticket.priority === 'high' ? 'bg-red-100 text-red-700' :
                    ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {ticket.priority === 'high' ? 'Haute' : ticket.priority === 'medium' ? 'Moyenne' : 'Basse'}
                  </div>
                  <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {ticket.reason}
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center text-gray-500 py-8">
                Aucun ticket bloquant
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Tableau des consultants */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-blue-500" />
          Performance des Consultants
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consultant</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fermés</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ouverts</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Termine</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Efficacité</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
              </tr>
            </thead>
<tbody className="bg-white divide-y divide-gray-200">
  {consultantsData.map((consultant) => {
    const avatarBg =
      consultant.efficiency >= 80
        ? 'bg-blue-600'
        : consultant.efficiency >= 60
        ? 'bg-blue-400'
        : 'bg-blue-300';

    return (
      <tr key={consultant.id} className="hover:bg-gray-50 transition-colors">
        <td className="flex items-center px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${avatarBg} text-white`}
            >
              {consultant.name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm font-medium text-gray-900">
              {consultant.name}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
          {consultant.tickets}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
          {consultant.closedTickets}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
          {consultant.inProgressTickets}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
          {consultant.deliveredTickets}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center">
          <div className="flex items-center justify-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ${
                consultant.efficiency >= 80
                  ? 'bg-green-500'
                  : consultant.efficiency >= 60
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`}
            >
              {consultant.efficiency}%
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center">
          <div
            className={`px-2 py-1 rounded-full text-xs font-semibold ${
              consultant.efficiency >= 80
                ? 'bg-green-100 text-green-800'
                : consultant.efficiency >= 60
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {consultant.efficiency >= 80
              ? 'Excellente'
              : consultant.efficiency >= 60
              ? 'Bonne'
              : 'À Améliorer'}
          </div>
        </td>
      </tr>
    );
  })}
</tbody>

          </table>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          <p>Note: L'efficacité est calculée sur la base des tickets fermés, respect des délais et rapidité de livraison.</p>
        </div>
      </div>
{/* nbr retour  */}
<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
  <div className="flex items-center justify-between mb-6">
    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
      <svg className="w-6 h-6 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      Retours par Développeur
    </h2>
    <div className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
      {consultantsData.length} Développeurs
    </div>
  </div>

  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Développeur
          </th>
          <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Total Retours
          </th>
          <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Performance
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {consultantsData.map((dev) => {
          // Déterminer la couleur en fonction du nombre de retours
          const getPerformanceColor = (retours) => {
            if (retours === 0) return 'bg-green-100 text-green-800';
            if (retours <= 2) return 'bg-yellow-100 text-yellow-800';
            return 'bg-red-100 text-red-800';
          };

          const getPerformanceText = (retours) => {
            if (retours === 0) return 'Excellent';
            if (retours <= 2) return 'Moyen';
            return 'À améliorer';
          };

          return (
            <tr key={dev.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                    {dev.name.charAt(0)}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{dev.name}</div>
                    <div className="text-sm text-gray-500">{dev.tickets} tickets</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  dev.totalRetours === 0 ? 'bg-green-100 text-green-800' : 
                  dev.totalRetours <= 2 ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-red-100 text-red-800'
                }`}>
                  {dev.totalRetours}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getPerformanceColor(dev.totalRetours)}`}>
                  {getPerformanceText(dev.totalRetours)}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>

  <div className="mt-4 flex justify-between items-center text-sm text-gray-500">
    <div>
      <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span>
      <span className="mr-3">0 retours</span>
      <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-1"></span>
      <span className="mr-3">1-2 retours</span>
      <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span>
      <span>3+ retours</span>
    </div>
    <div>
      <span className="text-gray-400">Dernière mise à jour: {new Date().toLocaleDateString('fr-FR')}</span>
    </div>
  </div>
</div>
    </div>
  );
};

export default Dashboard;